import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import auth from '@/helpers/auth';

interface Country {
    code: string;
    dial_code?: string;
    apply_vat?: boolean;
    is_eu?: boolean;
    [key: string]: unknown;
}

export interface EnrichedCountry extends Country {
    label: string;
}

const COUNTRIES_KEY = 'countries';

export function useCountries() {
    const { i18n } = useTranslation();
    const locale = i18n.language || 'en';

    // The regular endpoint is authenticated; the public register page reads
    // a trimmed list (code + dial code) from the unauthenticated one.
    const endpoint = auth.isAnonymous() ? '/api/register/countries' : '/api/countries';

    const { data: countries = [], isPending, refetch } = useQuery<Country[]>({
        queryKey: [
            COUNTRIES_KEY,
            endpoint,
        ],
        queryFn: () => {
            return axios.get<{ results: Country[] }>(endpoint).then(({ data }) => {
                return data.results;
            });
        },
        staleTime: 10 * 60 * 1000,
    });

    const displayNames = new Intl.DisplayNames([
        locale,
    ], { type: 'region' });

    const enriched: EnrichedCountry[] = countries.map((country) => {
        return {
            ...country,
            label: displayNames.of(country.code) ?? country.code,
        };
    });

    return {
        countries: enriched,
        isPending,
        refetch,
    };
}

export function useCreateCountry() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (values: Record<string, unknown>) => {
            return axios.post<Country>('/api/countries', values).then(({ data }) => {
                return data;
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [
                COUNTRIES_KEY,
            ] });
        },
    });
}

interface UpdateCountryInput {
    code: string;
    values: Record<string, unknown>;
}

export function useUpdateCountry() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ code, values }: UpdateCountryInput) => {
            return axios.patch<Country>(`/api/countries/${ code }`, values).then(({ data }) => {
                return data;
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [
                COUNTRIES_KEY,
            ] });
        },
    });
}
